import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { Menu, MenuOption } from 'components/Menu';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import { sortBy } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doAseSetupTemplateDelete,
  doAseSetupTemplateOpen,
} from 'store/actions';
import {
  selectRemoteAssessmentTemplates,
  selectRemoteComplianceSubtypesWithType,
} from 'store/remote/selectors';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SETTINGS_ASSESSMENTS
>;

export const TemplatesListScreen: React.FC<{
  navigation: Props['navigation'];
}> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const complianceSubtypes = useSelector(
    selectRemoteComplianceSubtypesWithType,
  )!;
  const templates = useSelector(selectRemoteAssessmentTemplates)!;

  const data = sortBy(
    Object.values(templates).map((template) => {
      const subtype = complianceSubtypes[template.complianceSubtypeId]!;
      return {
        id: template.id,
        name: template.name,
        enabled: template.enabled,
        complianceId: subtype.complianceTypeId,
        complianceName: subtype.complianceTypeName,
        complianceSubId: subtype.id,
        complianceSubName: subtype.name,
      };
    }),
    [
      'complianceName',
      'complianceId',
      'complianceSubName',
      'complianceSubId',
      'name',
    ],
  );

  const groupedData = data.reduce<
    {
      id: number;
      name: string;
      data: {
        id: number;
        name: string;
        data: {
          id: number;
          name: string;
          enabled: boolean;
        }[];
      }[];
    }[]
  >((acc, template, index, arr) => {
    const previous = arr[index - 1];
    let index1 = Math.max(acc.length - 1, 0);
    let index2 = 0;

    if (previous?.complianceId !== template.complianceId) {
      index1 = acc.length;
      index2 = 0;
      acc.push({
        id: template.complianceId,
        name: template.complianceName,
        data: [],
      });
    } else {
      index2 = Math.max(acc[index1]!.data!.length - 1, 0);
    }

    if (
      previous?.complianceId !== template.complianceId ||
      previous.complianceSubId !== template.complianceSubId
    ) {
      index2 = acc[index1]!.data.length;
      acc[index1]!.data.push({
        id: template.complianceSubId,
        name: template.complianceSubName,
        data: [],
      });
    }

    acc[index1]!.data[index2]!.data.push({
      id: template.id,
      name: template.name,
      enabled: template.enabled,
    });

    return acc;
  }, []);

  const onNewTemplatePress = ({
    complianceSubtypeId,
  }: {
    complianceSubtypeId: number;
  }) =>
    dispatch(
      doAseSetupTemplateOpen({
        id: undefined,
        newData: {
          complianceSubtypeId,
        },
      }),
    );

  const onTemplatePress = React.useCallback(
    (id: number) =>
      dispatch(
        doAseSetupTemplateOpen({
          id,
        }),
      ),
    [dispatch],
  );

  const onTemplateDeletePress = React.useCallback(
    (id: number) =>
      dispatch(
        doAseSetupTemplateDelete({
          id,
        }),
      ),
    [dispatch],
  );

  const renderItem = React.useCallback(
    (item: { id: number; name: string; enabled: boolean }) => {
      return (
        <CardRow
          key={item.id}
          label={item.name}
          value={item.enabled ? undefined : 'Disabled'}
          onPress={() => onTemplatePress(item.id)}
          indentActions
          menuOptions={[
            {
              text: 'Open Template',
              icon: ICONS.CircleInfoLight,
              onPress: () => onTemplatePress(item.id),
            },
            {
              text: 'Delete Template',
              icon: ICONS.TrashCanLight,
              destructive: true,
              onPress: () => onTemplateDeletePress(item.id),
            },
          ]}
        />
      );
    },
    [onTemplateDeletePress, onTemplatePress],
  );

  const newMenuOptions = sortBy(Object.values(complianceSubtypes), [
    'complianceTypeName',
    'name',
  ]).reduce<MenuOption[]>((acc, type, index, arr) => {
    const previousComplianceTypeName = arr[index - 1]?.complianceTypeName;
    if (type.complianceTypeName !== previousComplianceTypeName) {
      acc.push(type.complianceTypeName);
    }
    acc.push({
      key: `${type.id}`,
      text: type.name,
      onPress: () => onNewTemplatePress({ complianceSubtypeId: type.id }),
      icon: ICONS.PlusLight,
    });
    return acc;
  }, []);

  return (
    <Page
      scrollView={!!groupedData.length}
      header={
        <PageHeader
          title="Assessment Templates"
          onPressBackButton={
            screenSize === ScreenSize.small ? navigation.goBack : undefined
          }
          menuRight={{
            iconButtonConfig: {
              icon: ICONS.PlusLight,
            },
            options: newMenuOptions,
          }}
        />
      }
    >
      {groupedData.length ? (
        groupedData.map((complianceType) => (
          <React.Fragment key={complianceType.id}>
            <CardGeneric>
              <Txt size="xlarge">{complianceType.name}</Txt>
            </CardGeneric>
            {complianceType.data.map((complianceSubtype) => (
              <Card key={complianceSubtype.id} label={complianceSubtype.name}>
                {complianceSubtype.data.map(renderItem)}
              </Card>
            ))}
          </React.Fragment>
        ))
      ) : (
        <PageEmpty icon={ICONS.RBPlectrumLight}>
          <Menu
            buttonConfig={{ text: 'New Template' }}
            options={newMenuOptions}
          />
        </PageEmpty>
      )}
    </Page>
  );
};
